import {
  Button,
  ButtonSize,
  IconChevronDown,
  IconChevronUp,
} from '@leland-dev/leland-ui-library';
import Link from 'next/link';
import { type FC, useEffect, useRef, useState } from 'react';

export interface TableOfContentsEntry {
  id: string;
  text: string;
}

interface TableOfContentsProps {
  entries: TableOfContentsEntry[];
  defaultCollapsed: boolean;
  activeSectionId: Possible<TableOfContentsEntry['id']>;
  handleNavigateToContent: (entry: TableOfContentsEntry['id']) => void;
}

const TableOfContents: FC<TableOfContentsProps> = ({
  entries,
  defaultCollapsed,
  activeSectionId,
  handleNavigateToContent,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  const activeEntry = entries.find((entry) => entry.id === activeSectionId);

  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!defaultCollapsed && rootRef.current) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            setIsCollapsed(true);
            observer.disconnect();
          }
        },
        { rootMargin: '0px 0px -90% 0px', threshold: 0.1 },
      );

      observer.observe(rootRef.current);

      return () => observer.disconnect();
    }
  }, [defaultCollapsed]);

  return (
    <div
      ref={rootRef}
      className="group flex max-h-80 w-full flex-col gap-2 rounded-lg bg-leland-gray-hover p-4 lg:w-96"
    >
      <div
        className={`flex min-h-[3.25rem] items-center justify-between space-x-2 border-leland-gray-stroke ${isCollapsed ? '' : 'border-b'}`}
      >
        <div className="size-full grow truncate">
          <h4
            className={`font-medium transition-all ${isCollapsed && activeEntry ? 'text-leland-gray-light' : 'text-xl text-leland-gray-dark'}`}
          >
            Table of Contents
          </h4>
          {activeEntry && isCollapsed ? (
            <h4 className="truncate text-xl font-medium text-leland-gray-dark">
              {activeEntry.text}
            </h4>
          ) : null}
        </div>
        <Button
          LeftIcon={isCollapsed ? IconChevronDown : IconChevronUp}
          label="Expand Table of Contents"
          size={ButtonSize.SMALL}
          hideLabel
          onClick={() => setIsCollapsed((prev) => !prev)}
          id="table_of_contents--expand_table_of_contents"
        />
      </div>
      <ul
        className={`no-scrollbar space-y-3 overflow-y-auto ${isCollapsed ? 'hidden' : 'block'}`}
      >
        {entries.map(({ id, text }, index) => {
          const isActive = id === activeSectionId;
          return (
            <li key={index}>
              <Link
                onClick={(e) => {
                  e.preventDefault();
                  handleNavigateToContent(id);
                }}
                href={`#${id}`}
                scroll={true}
                className={`flex h-auto w-full text-left text-lg font-medium leading-tight ${isActive ? 'text-leland-gray-dark' : 'text-leland-gray-light hover:opacity-50'}`}
                id="table_of_contents--view_section"
              >
                {text}
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default TableOfContents;
